/**
 * Structured error codes returned by scan/aggregation API endpoints.
 *
 * Mirrors `services/scan_aggregation.py::AggregationError.code`.
 * Frontend uses these to render a localized message + severity icon
 * (⚠ = recoverable, ✕ = hard failure) without relying on the raw text.
 */
export type ScanErrorCode =
  // Format / nomenclature
  | 'invalid_format'
  | 'wrong_group'
  | 'structure_not_configured'
  | 'unknown_km'
  // Duplicates
  | 'duplicate_in_node'
  | 'duplicate_in_other_node'
  | 'duplicate_in_other_box'
  | 'duplicate_in_task'
  // Task lookup
  | 'task_not_found'
  | 'wrong_task_mode'
  // Aggregate node state
  | 'node_not_found'
  | 'node_closed'
  | 'node_empty'
  | 'node_underfilled'
  | 'node_submitted'
  | 'node_already_open'
  | 'node_must_be_closed'
  | 'reopen_forbidden'
  | 'level_not_supported'
  | 'level_not_scan_mode'
  // Box state
  | 'box_not_found'
  | 'box_closed'
  | 'km_not_in_box'
  | 'km_not_in_node'
  // KA (aggregation code) state
  | 'ka_not_found'
  | 'ka_must_be_closed'
  | 'ka_already_nested'

/** Severity hint for UI rendering. */
export type ScanErrorSeverity = 'warning' | 'error'

/** Map of scan error codes to severity. Recoverable issues use 'warning'. */
export const SCAN_ERROR_SEVERITY: Record<ScanErrorCode, ScanErrorSeverity> = {
  // Recoverable: user can retry with another code
  invalid_format: 'warning',
  wrong_group: 'warning',
  structure_not_configured: 'warning',
  unknown_km: 'warning',
  duplicate_in_node: 'warning',
  duplicate_in_other_node: 'warning',
  duplicate_in_other_box: 'warning',
  duplicate_in_task: 'warning',
  km_not_in_box: 'warning',
  km_not_in_node: 'warning',
  // Hard: state inconsistency, requires reload / different action
  task_not_found: 'error',
  wrong_task_mode: 'error',
  node_not_found: 'error',
  node_closed: 'error',
  node_empty: 'error',
  node_underfilled: 'error',
  node_submitted: 'error',
  node_already_open: 'error',
  node_must_be_closed: 'error',
  reopen_forbidden: 'error',
  level_not_supported: 'error',
  level_not_scan_mode: 'error',
  box_not_found: 'error',
  box_closed: 'error',
  ka_not_found: 'error',
  ka_must_be_closed: 'error',
  ka_already_nested: 'error',
}

export interface ScanErrorInfo {
  message: string
  code?: ScanErrorCode | string
  details?: Record<string, unknown>
}

/** Returns 'warning' (⚠) or 'error' (✕) — defaults to 'error' for unknown codes. */
export function severityForScanError(code?: string): ScanErrorSeverity {
  if (code && code in SCAN_ERROR_SEVERITY) {
    return SCAN_ERROR_SEVERITY[code as ScanErrorCode]
  }
  return 'error'
}
